import React, { useState, useEffect, useCallback } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Trash2, Receipt, Download, Upload } from 'lucide-react';
import { fmt } from '../../lib/accountingStore';
import { apiExpenses, apiVendors } from '../../lib/api';
import { toCSV, downloadFile, parseCSV, readFileAsText } from '../../lib/importExport';
import { toast } from 'sonner';
import { todayISO } from '../../lib/dates';

const CATS = ['Chemicals & Supplies', 'Fuel', 'Payroll', 'Insurance', 'Office Supplies', 'Equipment', 'Vehicle Maintenance', 'Marketing', 'Software', 'Utilities', 'Other'];

export default function Expenses() {
  const [expenses, setExpenses] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    apiExpenses.list().then((d) => setExpenses(d || [])).catch(() => setExpenses([]));
    apiVendors.list().then((d) => setVendors(d || [])).catch(() => setVendors([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  const onDelete = async (id) => { try { await apiExpenses.remove(id); load(); toast.success('Expense deleted'); } catch { toast.error('Failed'); } };
  const total = expenses.reduce((a, e) => a + (Number(e.amount) || 0), 0);
  const byCat = {};
  expenses.forEach((e) => { byCat[e.category] = (byCat[e.category] || 0) + (Number(e.amount) || 0); });
  const sortedCats = Object.entries(byCat).sort((a, b) => b[1] - a[1]);

  return (
    <div className="p-6 lg:p-8 space-y-5" data-testid="acct-expenses">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div><h2 className="text-2xl font-extrabold tracking-tight text-slate-900">Expenses</h2><p className="text-slate-500 text-sm">{expenses.length} expenses · {fmt(total)} total this period</p></div>
        <Button onClick={() => setOpen(true)} className="bg-[#8BC53F] hover:bg-[#6FA82C] gap-1.5" data-testid="new-expense-btn"><Plus className="w-4 h-4" />New Expense</Button>
      </div>
      <div className="flex gap-2 -mt-2">
        <label className="inline-flex items-center gap-1.5 px-3 h-9 bg-white border border-slate-200 rounded-md text-xs font-semibold text-slate-700 hover:bg-slate-50 cursor-pointer"><Upload className="w-3.5 h-3.5" />Import CSV
          <input type="file" accept=".csv" className="hidden" onChange={async (e) => {
            const f = e.target.files?.[0]; if (!f) return;
            const parsed = parseCSV(await readFileAsText(f));
            const docs = parsed.map((r, i) => ({ id: r.ID || `EXP-${Date.now()}-${i}`, date: r.Date || todayISO(), vendorId: r['Vendor ID'] || '', vendorName: r.Vendor || '', category: r.Category || 'Other', amount: Number(r.Amount) || 0, method: r.Method || 'Card', memo: r.Memo || '' }));
            try { await apiExpenses.bulk(docs); load(); toast.success(`Imported ${docs.length} expenses`); } catch { toast.error('Import failed'); }
            e.target.value = '';
          }} /></label>
        <Button size="sm" variant="outline" onClick={() => { downloadFile('xpertpests-expenses.csv', toCSV(expenses, [{ key: 'id', header: 'ID' }, { key: 'date', header: 'Date' }, { key: 'vendorName', header: 'Vendor' }, { key: 'category', header: 'Category' }, { key: 'amount', header: 'Amount' }, { key: 'method', header: 'Method' }, { key: 'memo', header: 'Memo' }])); toast.success('Exported expenses.csv'); }} className="gap-1.5 h-9"><Download className="w-3.5 h-3.5" />Export CSV</Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <Card className="lg:col-span-2 border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs text-slate-500 font-semibold uppercase tracking-wider">
              <tr><th className="px-5 py-3 text-left">Date</th><th className="px-5 py-3 text-left">Vendor</th><th className="px-5 py-3 text-left">Category</th><th className="px-5 py-3 text-left">Method</th><th className="px-5 py-3 text-right">Amount</th><th className="px-5 py-3"></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {expenses.map((e) => (
                <tr key={e.id} className="hover:bg-slate-50">
                  <td className="px-5 py-4 text-slate-600">{e.date}</td>
                  <td className="px-5 py-4"><div className="font-semibold text-slate-900">{e.vendorName || vendors.find((v) => v.id === e.vendorId)?.name || '—'}</div><div className="text-xs text-slate-500">{e.memo}</div></td>
                  <td className="px-5 py-4 text-slate-600">{e.category}</td>
                  <td className="px-5 py-4 text-slate-600">{e.method}</td>
                  <td className="px-5 py-4 text-right font-bold text-red-600 tabular-nums">−{fmt(e.amount)}</td>
                  <td className="px-5 py-4 text-right"><Button size="sm" variant="ghost" onClick={() => onDelete(e.id)}><Trash2 className="w-4 h-4 text-red-600" /></Button></td>
                </tr>
              ))}
              {expenses.length === 0 && <tr><td colSpan={6} className="text-center py-10 text-slate-500"><Receipt className="w-8 h-8 text-slate-300 mx-auto mb-2" />No expenses yet</td></tr>}
            </tbody>
          </table>
        </Card>
        <Card className="p-5 border-slate-200">
          <h3 className="font-bold text-slate-900 mb-4">By Category</h3>
          <div className="space-y-3">
            {sortedCats.map(([cat, amt]) => (
              <div key={cat}>
                <div className="flex justify-between text-sm mb-1"><span className="text-slate-700">{cat}</span><span className="font-semibold tabular-nums text-slate-900">{fmt(amt)}</span></div>
                <div className="h-2 bg-slate-100 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-[#8BC53F] to-[#6FA82C]" style={{ width: `${total ? (amt / total) * 100 : 0}%` }} /></div>
              </div>
            ))}
            {sortedCats.length === 0 && <p className="text-sm text-slate-400">No data yet.</p>}
          </div>
        </Card>
      </div>
      <ExpenseDialog open={open} setOpen={setOpen} vendors={vendors} onSave={load} />
    </div>
  );
}

function ExpenseDialog({ open, setOpen, vendors, onSave }) {
  const [vendorId, setVendorId] = useState('');
  const [date, setDate] = useState(todayISO());
  const [category, setCategory] = useState(CATS[0]);
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState('Card');
  const [memo, setMemo] = useState('');
  const submit = async () => {
    if (!amount) { toast.error('Amount required'); return; }
    const vendorName = vendors.find((v) => v.id === vendorId)?.name || '';
    try { await apiExpenses.create({ id: `EXP-${Date.now().toString(36)}`, date, vendorId, vendorName, category, amount: Number(amount), method, memo }); onSave(); setOpen(false); setAmount(''); setMemo(''); toast.success('Expense recorded'); } catch { toast.error('Failed'); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent data-testid="expense-dialog">
        <DialogHeader><DialogTitle>New Expense</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" /></div>
            <div><Label>Amount</Label><Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5" data-testid="expense-amount-input" /></div>
          </div>
          <div><Label>Vendor</Label><Select value={vendorId} onValueChange={setVendorId}><SelectTrigger className="mt-1.5"><SelectValue placeholder="Optional…" /></SelectTrigger><SelectContent>{vendors.map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent></Select></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Category</Label><Select value={category} onValueChange={setCategory}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></div>
            <div><Label>Payment Method</Label><Select value={method} onValueChange={setMethod}><SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="Card">Card</SelectItem><SelectItem value="Check">Check</SelectItem><SelectItem value="ACH">ACH</SelectItem><SelectItem value="Cash">Cash</SelectItem></SelectContent></Select></div>
          </div>
          <div><Label>Memo</Label><Input value={memo} onChange={(e) => setMemo(e.target.value)} className="mt-1.5" placeholder="Notes…" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-[#8BC53F] hover:bg-[#6FA82C]" data-testid="expense-save-btn">Save Expense</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
